import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { prop } from 'ramda';

import { handleVerifyClick } from '@core/js/components/main/Jumio/helpers';
import { useTranslations } from '@core/js/hooks/i18n';
import { useNavigate } from '@core/js/hooks/navigate';
import { useI18nSelector } from '@core/js/store/hooks';

import { useDynamicTagList } from '@interlayer/js/custom/hooks/useDynamicTagList';
import { useDynamicTags } from '@interlayer/js/custom/hooks/useDynamicTags';
import { useQueryParam } from '@interlayer/js/custom/hooks/useQueryParam';
import { QUERY_PARAMS } from '@interlayer/js/custom/hooks/useQueryParam/constants';
import { useCustomUserSelector } from '@interlayer/js/custom/stores/user';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/custom/utils/constants';

import { TAG_NAME } from '../constants';
import * as actions from '../store/actions';

const DYNAMIC_TAGS = [
    DYNAMIC_TAG_NAMES.VERIFICATION_REQUIRED_URL,
    DYNAMIC_TAG_NAMES.DISABLE_JUMIO_VERIFY_POPUP
];

export const useJumioVerifyPopup = () => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const translations = useI18nSelector(prop('translations'));
    const translate = useTranslations('playtech.mobile.system.jumio.');
    const hasTranslationsLoaded = Object.keys(translations).length > 0;
    const showJumioVerifyPopup = useCallback(popupData => dispatch(actions.showJumioVerifyPopupAction(popupData)), [dispatch]);

    const isJumioShown = useRef(false);
    const isHideKYC = useQueryParam(QUERY_PARAMS.isHideKYC, false);
    const allPlayerTags = useCustomUserSelector(prop('allPlayerTags'));
    const navigate = useNavigate({ replace: true });
    const [
        dTagVerificationRequiredUrl,
        dTagDisableJumioVerifyPopup
    ] = useDynamicTags(DYNAMIC_TAGS);
    const dTagValuesTagForJumioPopup = useDynamicTagList({
        tagName: DYNAMIC_TAG_NAMES.VALUES_TAG_FOR_JUMIO_POPUP,
        defaultValue: 'mandatory,pending'
    });

    useEffect(() => {
        const tagValue = allPlayerTags?.[TAG_NAME]?.value;
        const isNoUploadRequirementTags = dTagValuesTagForJumioPopup?.every(tagForJumioPopup => tagValue !== tagForJumioPopup);

        if (
            dTagDisableJumioVerifyPopup
            || !hasTranslationsLoaded
            || !tagValue
            || isHideKYC
            || isNoUploadRequirementTags
            || isJumioShown.current
        ) {
            if (isJumioShown.current && isNoUploadRequirementTags) {
                window.onpopstate = null;
                isJumioShown.current = false;
            }

            if (dTagVerificationRequiredUrl && dTagVerificationRequiredUrl.trim() !== pathname && !isNoUploadRequirementTags) {
                navigate(dTagVerificationRequiredUrl);
            }

            return;
        }

        isJumioShown.current = true;

        if (dTagVerificationRequiredUrl) {
            navigate(dTagVerificationRequiredUrl);
            window.history.pushState(null, null, window.location.href);
            window.onpopstate = function() {
                window.history.go(1);
            };

            return;
        }

        showJumioVerifyPopup({
            content: translate('content'),
            onAccept: handleVerifyClick
        });
    }, [
        allPlayerTags,
        isHideKYC,
        dTagDisableJumioVerifyPopup,
        dTagValuesTagForJumioPopup,
        dTagVerificationRequiredUrl,
        hasTranslationsLoaded,
        isJumioShown,
        navigate,
        showJumioVerifyPopup,
        translate,
        pathname
    ]);
};
